import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Send, CheckCircle, AlertCircle, Upload, X, Edit3, Save, Plus, Trash2, ChevronDown, Search } from 'lucide-react';
import { apiFetch } from '../api';
import { useWebSocket } from '../websocket/WebSocketContext';
import { ensureSocketConnection } from '../websocket/socketUtils';

export default function EmailForm({ setIsLoading, setSuccess, setError, fetchStats, API_BASE_URL, loginId, deliveryMode }) {
  const { client, connected } = useWebSocket();
  const [recipient, setRecipient] = useState(''); // to
  const [subject, setSubject] = useState(''); // subject
  const [message, setMessage] = useState(''); // body
  const [app_name, setApp_name] = useState(''); // app_name
  const [appNames, setAppNames] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAppNamesLoading, setIsAppNamesLoading] = useState(false);
  const [cc, setCc] = useState(''); // cc
  const [bcc, setBcc] = useState(''); // bcc
  const [email_attachment, setEmail_attachment] = useState('NO'); // email_attachment
  const [email_attachment_compress, setEmail_attachment_compress] = useState('NO'); // email_attachment_compress
  const [attachment_format, setAttachment_format] = useState('xlsx'); // attachment_format
  const [bodyType, setBodyType] = useState('N');
  const [isUploading, setIsUploading] = useState(false);

  // Attachment data arrays
  const [attachment_data, setAttachment_data] = useState([]);
  const [attachment_data_1, setAttachment_data_1] = useState([]);
  const [attachment_data_2, setAttachment_data_2] = useState([]);

  // Edit modes for attachment data
  const [editingAttachment, setEditingAttachment] = useState({ type: null, index: null });
  const [editData, setEditData] = useState({});

  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [successLocal, setSuccessLocal] = useState('');
  const [errorLocal, setErrorLocal] = useState('');

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Clear messages after 3 seconds
  React.useEffect(() => {
    if (successLocal || errorLocal) {
      const timer = setTimeout(() => {
        setSuccessLocal('');
        setErrorLocal('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successLocal, errorLocal]);

  // Helper functions for formatting
  const formatKey = (key) => {
    return key.replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace(/-/g, ' ');
  };

  // Filter app names based on search term
  const filteredAppNames = appNames.filter(name =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  const formatValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'object') {
      try { return JSON.stringify(value, null, 2); } catch { return String(value); }
    }
    return value;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  const fetchAppNames = async () => {
    setIsAppNamesLoading(true);
    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setErrorLocal('Socket connection failed.');
          setIsAppNamesLoading(false);
          return;
        }

        const topic = `/topic/getEmailAppName/${loginId}`;

        const subscription = client.subscribe(topic, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

            if (data && data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
              const names = data.Response.map(item => item.appName).filter(Boolean);
              setAppNames(names);

              if (names.length > 0 && !app_name) {
                setApp_name(names[0]);
              }
            } else {
              setErrorLocal('Failed to load app names via socket.');
            }
          } catch (e) {
            console.error('Failed to parse app names', e);
            setErrorLocal('Failed to parse app names.');
          } finally {
            subscription.unsubscribe();
            setIsAppNamesLoading(false);
          }
        });

        client.publish({
          destination: `/app/getEmailAppName`,
          body: JSON.stringify({}) // Assuming empty body for get-all
        });

      } else {
        // --- API LOGIC ---
        const response = await apiFetch(`/getEmailAppName`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
          const appNames = data.Response.map(item => item.appName).filter(Boolean);
          setAppNames(appNames);

          if (appNames.length > 0 && !app_name) {
            setApp_name(appNames[0]);
          }
        } else {
          setErrorLocal('Failed to load app names.');
        }
        setIsAppNamesLoading(false);
      }
    } catch (err) {
      console.error("Error fetching app names:", err);
      setErrorLocal('Failed to load app names. Network error.');
      setIsAppNamesLoading(false);
    }
  };

  // Initial fetch of app names
  useEffect(() => {
    fetchAppNames();
  }, [API_BASE_URL]);

  // Handle file upload for JSON
  const handleFileUpload = (event, attachmentType) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      setErrorLocal('Please upload a valid JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);

        if (!Array.isArray(jsonData)) {
          setErrorLocal('JSON file must contain an array of objects');
          return;
        }

        if (attachmentType === 'attachment_data') {
          setAttachment_data(jsonData);
        } else if (attachmentType === 'attachment_data_1') {
          setAttachment_data_1(jsonData);
        } else if (attachmentType === 'attachment_data_2') {
          setAttachment_data_2(jsonData);
        }

        setSuccessLocal(`${attachmentType} loaded successfully with ${jsonData.length} items`);
      } catch (error) {
        setErrorLocal('Invalid JSON file format');
      }
    };
    reader.readAsText(file);
  };

  // Add new item to attachment data
  const addNewItem = (attachmentType) => {
    let newItem = {};

    // Get the data array to determine structure
    const currentData = attachmentType === 'attachment_data' ? attachment_data :
      attachmentType === 'attachment_data_1' ? attachment_data_1 :
        attachment_data_2;

    if (currentData.length > 0) {
      // Create new item with same keys as first item but empty values
      Object.keys(currentData[0]).forEach(key => {
        newItem[key] = '';
      });
    } else {
      // Default structure if no data exists
      newItem = { name: '', age: '', mobileNo: '' };
    }

    if (attachmentType === 'attachment_data') {
      setAttachment_data([...attachment_data, newItem]);
    } else if (attachmentType === 'attachment_data_1') {
      setAttachment_data_1([...attachment_data_1, newItem]);
    } else if (attachmentType === 'attachment_data_2') {
      setAttachment_data_2([...attachment_data_2, newItem]);
    }
  };

  // Remove item from attachment data
  const removeItem = (attachmentType, index) => {
    if (attachmentType === 'attachment_data') {
      setAttachment_data(attachment_data.filter((_, i) => i !== index));
    } else if (attachmentType === 'attachment_data_1') {
      setAttachment_data_1(attachment_data_1.filter((_, i) => i !== index));
    } else if (attachmentType === 'attachment_data_2') {
      setAttachment_data_2(attachment_data_2.filter((_, i) => i !== index));
    }
  };

  // Start editing an item
  const startEditing = (type, index, item) => {
    setEditingAttachment({ type, index });
    setEditData({ ...item });
  };

  // Update edit data
  const updateEditData = (key, value) => {
    setEditData({
      ...editData,
      [key]: value
    });
  };

  const handleAppSelect = (appName) => {
    setApp_name(appName);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  // Save edited item
  const saveEdit = () => {
    const { type, index } = editingAttachment;

    if (type === 'attachment_data') {
      const updated = [...attachment_data];
      updated[index] = editData;
      setAttachment_data(updated);
    } else if (type === 'attachment_data_1') {
      const updated = [...attachment_data_1];
      updated[index] = editData;
      setAttachment_data_1(updated);
    } else if (type === 'attachment_data_2') {
      const updated = [...attachment_data_2];
      updated[index] = editData;
      setAttachment_data_2(updated);
    }

    setEditingAttachment({ type: null, index: null });
    setEditData({});
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingAttachment({ type: null, index: null });
    setEditData({});
  };

  // Clear attachment data
  const clearAttachmentData = (attachmentType) => {
    if (attachmentType === 'attachment_data') {
      setAttachment_data([]);
    } else if (attachmentType === 'attachment_data_1') {
      setAttachment_data_1([]);
    } else if (attachmentType === 'attachment_data_2') {
      setAttachment_data_2([]);
    }
  };

  // Render attachment data section with dynamic JSON support
  const renderAttachmentData = (data, type, title) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-white/80 font-medium">{title}</h4>
        <div className="flex space-x-2">
          <label className="cursor-pointer bg-blue-500/20 hover:bg-blue-500/30 px-3 py-1 rounded-lg text-blue-200 text-sm transition-colors flex items-center space-x-1">
            <Upload className="w-4 h-4" />
            <span>Upload JSON</span>
            <input
              type="file"
              accept=".json"
              onChange={(e) => handleFileUpload(e, type)}
              className="hidden"
            />
          </label>
          <button
            onClick={() => addNewItem(type)}
            className="bg-green-500/20 hover:bg-green-500/30 px-3 py-1 rounded-lg text-green-200 text-sm transition-colors flex items-center space-x-1"
          >
            <Plus className="w-4 h-4" />
            <span>Add</span>
          </button>
          {data.length > 0 && (
            <button
              onClick={() => clearAttachmentData(type)}
              className="bg-red-500/20 hover:bg-red-500/30 px-3 py-1 rounded-lg text-red-200 text-sm transition-colors flex items-center space-x-1"
            >
              <X className="w-4 h-4" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>

      {data.length > 0 && (
        <div className="bg-white/5 rounded-xl p-4 max-h-80 overflow-y-auto">
          <div className="text-sm text-white/60 mb-4 flex justify-between">
            <span>Items: {data.length}</span>
            <span>Fields: {Object.keys(data[0]).length}</span>
          </div>

          {data.map((item, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-3 mb-3 last:mb-0">
              {editingAttachment.type === type && editingAttachment.index === index ? (
                // Edit Mode
                <div className="space-y-3">
                  <div className="text-xs text-white/60 mb-3">Editing Item #{index + 1}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.keys(item).map((key) => (
                      <div key={key} className="space-y-1">
                        <label className="text-xs text-white/70 font-medium">
                          {formatKey(key)}
                        </label>
                        <input
                          type="text"
                          value={editData[key] || ''}
                          onChange={(e) => updateEditData(key, e.target.value)}
                          placeholder={`Enter ${formatKey(key)}`}
                          className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white placeholder-white/50 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex space-x-2 pt-2">
                    <button
                      onClick={saveEdit}
                      className="bg-green-500/20 hover:bg-green-500/30 px-2 py-1 rounded text-green-200 text-xs flex items-center space-x-1"
                    >
                      <Save className="w-3 h-3" />
                      <span>Save</span>
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-500/20 hover:bg-gray-500/30 px-2 py-1 rounded text-gray-200 text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // Display Mode
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-xs text-white/60 mb-2">Item #{index + 1}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {Object.entries(item).map(([key, value]) => (
                        <div key={key} className="bg-white/5 rounded-lg p-2">
                          <div className="text-xs text-white/60 font-medium mb-1">
                            {formatKey(key)}
                          </div>
                          <div className="text-sm text-white/90 font-mono break-all">
                            {typeof value === 'object' && value !== null
                              ? <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
                              : (formatValue(value) || <span className="text-white/40 italic">Empty</span>)
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex space-x-1 ml-3">
                    <button
                      onClick={() => startEditing(type, index, item)}
                      className="bg-blue-500/20 hover:bg-blue-500/30 p-1 rounded text-blue-200"
                      title="Edit Item"
                    >
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => removeItem(type, index)}
                      className="bg-red-500/20 hover:bg-red-500/30 p-1 rounded text-red-200"
                      title="Delete Item"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleSendEmail = async () => {
    setIsLoading(true);
    setIsLoadingLocal(true);
    setSuccessLocal('');
    setErrorLocal('');

    // Basic validation
    if (!app_name || !subject || !message || !recipient || !bodyType) {
      setErrorLocal('Please fill in App Name, Subject, Message, and Email Address fields.');
      setIsLoading(false);
      setIsLoadingLocal(false);
      return;
    }

    // Build payload
    const payload = {
      appName: app_name,
      to: recipient,
      subject: subject,
      body: message,
      body_type: bodyType,
      email_attachment: email_attachment
    };

    if (cc.trim()) payload.cc = cc;
    if (bcc.trim()) payload.bcc = bcc;

    if (email_attachment === 'YES' || email_attachment === 'Y' || email_attachment === '1') {
      payload.email_attachment_compress = email_attachment_compress;
      payload.attachment_format = attachment_format;
      if (attachment_data.length > 0) payload.attachment_data = attachment_data;
      if (attachment_data_1.length > 0) payload.attachment_data_1 = attachment_data_1;
      if (attachment_data_2.length > 0) payload.attachment_data_2 = attachment_data_2;
    }

    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setErrorLocal('Socket connection failed.');
          setIsLoading(false);
          setIsLoadingLocal(false);
          return;
        }

        // 1. Subscribe to response
        // Assumption: Backend sends confirmation to this topic
        const topic = `/topic/email/send/${loginId}`;

        const subscription = client.subscribe(topic, (msg) => {
          try {
            const data = JSON.parse(msg.body);
            if (data.STATUS === 'SUCCESS') {
              setSuccessLocal('Email added to queue successfully (Socket)!');
              setSuccess('Email sent!');

              // Clear fields
              setSubject('');
              setMessage('');
              setRecipient('');
              setApp_name('');
              setCc('');
              setBcc('');
              setEmail_attachment('NO');
              setEmail_attachment_compress('NO');
              setBodyType('N');
              setAttachment_format('xlsx');
              setAttachment_data([]);
              setAttachment_data_1([]);
              setAttachment_data_2([]);

              fetchStats('email');
            } else {
              const errMsg = data.MESSAGE || 'An unknown error occurred.';
              setErrorLocal(`Failed to send Email. ${errMsg}`);
              setError(`Failed to send Email: ${errMsg}`);
            }
          } catch (e) {
            console.error('Failed to parse socket send response', e);
            setErrorLocal('Failed to parse server response.');
          } finally {
            subscription.unsubscribe();
            setIsLoading(false);
            setIsLoadingLocal(false);
          }
        });

        // 2. Publish Payload
        client.publish({
          destination: `/app/email/send`,
          body: JSON.stringify(payload)
        });

      } else {
        // --- API LOGIC ---
        const response = await apiFetch(`/email/send`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (data.STATUS === 'SUCCESS') {
          setSuccessLocal('Email added to queue successfully!');
          setSuccess('Email sent!');
          // Clear fields
          setSubject('');
          setMessage('');
          setRecipient('');
          setApp_name('');
          setCc('');
          setBcc('');
          setEmail_attachment('NO');
          setEmail_attachment_compress('NO');
          setBodyType('N');
          setAttachment_format('xlsx');
          setAttachment_data([]);
          setAttachment_data_1([]);
          setAttachment_data_2([]);
          fetchStats('email');
        } else {
          setErrorLocal(`Failed to send Email. ${data.MESSAGE || 'An unknown error occurred.'}`);
          setError(`Failed to send Email: ${data.MESSAGE || 'Unknown error'}`);
        }
        setIsLoading(false);
        setIsLoadingLocal(false);
      }
    } catch (err) {
      console.error("Email Send error:", err);
      setErrorLocal('Failed to send Email. Network error or invalid input.');
      setError('Network error or invalid input for Email.');
      setIsLoading(false);
      setIsLoadingLocal(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {(successLocal || errorLocal) && (
        <div className={`mb-6 p-4 rounded-xl flex items-center space-x-2 ${successLocal ? 'bg-green-500/20 border border-green-500/50 text-green-200' : 'bg-red-500/20 border border-red-500/50 text-red-200'
          }`}>
          {successLocal ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{successLocal || errorLocal}</span>
        </div>
      )}

      {/* App Name */}
      <div>
        <label className="block text-white/80 text-sm font-medium mb-2">
          App Name *
        </label>
        {isAppNamesLoading ? (
          <div className="w-full px-4 py-4 bg-white/5 border border-white/20 rounded-xl text-white flex items-center">
            <Loader2 className="w-5 h-5 mr-3 animate-spin text-blue-400" />
            <span>Loading available applications...</span>
          </div>
        ) : appNames.length > 0 ? (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 flex items-center justify-between font-medium hover:bg-white/15"
              disabled={isLoadingLocal || isUploading}
            >
              <span>{app_name || 'Select an app...'}</span>
              <ChevronDown className={`w-5 h-5 text-white/60 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                  onClick={() => setIsDropdownOpen(false)}
                />

                {/* Dropdown Content */}
                <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900 backdrop-blur-xl border border-red/30 rounded-xl shadow-2xl z-[9999] overflow-hidden">
                  {/* Search Input */}
                  <div className="p-3 border-b border-white/10">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search apps..."
                        className="w-full pl-10 pr-8 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 hover:text-white/80"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* App List with Scroll */}
                  <div className="max-h-60 overflow-y-auto custom-scrollbar">
                    {filteredAppNames.length > 0 ? (
                      filteredAppNames.map((name) => (
                        <button
                          key={name}
                          onClick={() => handleAppSelect(name)}
                          className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors duration-200 ${app_name === name
                            ? "bg-blue-500/20 text-blue-200"
                            : "text-white/80"
                            }`}
                        >
                          {name}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-white/50 text-sm">
                        No apps found matching "{searchTerm}"
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <input
            type="text"
            value={app_name}
            onChange={(e) => setApp_name(e.target.value)}
            placeholder="Enter App Name (no apps found)"
            className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
            disabled={isLoadingLocal || isUploading}
          />
        )}
      </div>

      {/* Email Address (TO) */}
      <div>
        <label className="block text-white/80 text-sm font-medium mb-2">
          Email Address (TO) *
        </label>
        <input
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="user@example.com,user2@example.com"
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
          disabled={isLoadingLocal}
        />
        <p className="text-white/50 text-xs mt-1">Separate multiple emails with commas</p>
      </div>

      {/* CC */}
      <div>
        <label className="block text-white/80 text-sm font-medium mb-2">
          CC (Optional)
        </label>
        <input
          type="email"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          placeholder="cc@example.com,cc2@example.com"
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
          disabled={isLoadingLocal}
        />
      </div>

      {/* BCC */}
      <div>
        <label className="block text-white/80 text-sm font-medium mb-2">
          BCC (Optional)
        </label>
        <input
          type="email"
          value={bcc}
          onChange={(e) => setBcc(e.target.value)}
          placeholder="bcc@example.com,bcc2@example.com"
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
          disabled={isLoadingLocal}
        />
      </div>

      {/* Subject */}
      <div>
        <label className="block text-white/80 text-sm font-medium mb-2">
          Subject *
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Enter email subject..."
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
          disabled={isLoadingLocal}
        />
      </div>

      {/* Email Body Type */}
      <div>
        <label className="block text-white/80 text-sm font-medium mb-2">
          Email Body Type *
        </label>
        <select
          value={bodyType}
          onChange={(e) => setBodyType(e.target.value)}
          className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
          disabled={isLoadingLocal}
        >
          <option value="N">TEXT</option>
          <option value="Y">HTML</option>
        </select>
      </div>

      {/* Message */}
      <div>
        <label className="block text-white/80 text-sm font-medium mb-2">
          Message *
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter your email message..."
          rows={4}
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 resize-none"
          disabled={isLoadingLocal}
        />
      </div>

      {/* Email Attachment Settings */}
      <div className="bg-white/5 rounded-xl p-6 space-y-4">
        <h3 className="text-white font-semibold text-lg">Attachment Settings</h3>

        {/* Email Attachment Toggle */}
        <div>
          <label className="block text-white/80 text-sm font-medium mb-2">
            Enable Attachments
          </label>
          <select
            value={email_attachment}
            onChange={(e) => setEmail_attachment(e.target.value)}
            className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
            disabled={isLoadingLocal}
          >
            <option value="NO">NO</option>
            <option value="YES">YES</option>
          </select>
        </div>

        {/* Show attachment options only if attachments are enabled */}
        {(email_attachment === 'YES' || email_attachment === 'Y' || email_attachment === '1') && (
          <>
            {/* Attachment Format */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Attachment Format
              </label>
              <select
                value={attachment_format}
                onChange={(e) => setAttachment_format(e.target.value)}
                className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                disabled={isLoadingLocal}
              >
                <option value="xlsx">XLSX</option>
                <option value="xls">XLS</option>
                <option value="csv">CSV</option>
              </select>
            </div>

            {/* Email Attachment Compress */}
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Compress Attachments
              </label>
              <select
                value={email_attachment_compress}
                onChange={(e) => setEmail_attachment_compress(e.target.value)}
                className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                disabled={isLoadingLocal}
              >
                <option value="NO">NO</option>
                <option value="YES">YES</option>
              </select>
            </div>

            {/* Attachment Data Sections */}
            <div className="space-y-6">
              {renderAttachmentData(attachment_data, 'attachment_data', 'Attachment Data')}
              {renderAttachmentData(attachment_data_1, 'attachment_data_1', 'Attachment Data 1')}
              {renderAttachmentData(attachment_data_2, 'attachment_data_2', 'Attachment Data 2')}
            </div>
          </>
        )}
      </div>

      {/* Send Button */}
      <button
        onClick={handleSendEmail}
        disabled={isLoadingLocal || !app_name || !subject || !message || !recipient || !bodyType}
        className={`w-full py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-300 relative overflow-hidden bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 ${isLoadingLocal || !app_name || !subject || !message || !recipient ? 'opacity-50' : ''
          }`}
      >
        {isLoadingLocal ? (
          <div className="flex items-center justify-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Sending...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center space-x-2">
            <Send className="w-5 h-5" />
            <span>Send Email</span>
          </div>
        )}
      </button>
    </div>
  );
}